import PageTitle from "../Common/PageTitle"
import { FC, useEffect, useState } from "react"
import { Box, Chip, CircularProgress, Typography } from "@mui/material"
import { Apis } from "../../helpers"
import { PropertyProps } from "../../types/properties"
import { Connect, mapStateToProps } from "../../Redux"
import { LoadingButton } from '@mui/lab'
import { EventsEmit } from "../../configs/events"

const MAX_DATES = 3
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1)

interface Props {
    selectedProperty?: PropertyProps
}
interface StateProps {
    reminderDates: number[]
    loading: boolean
    saving: boolean
}
const ReminderDates: FC<Props> = ({
    selectedProperty
}) => {
    const [state, setState] = useState<StateProps>({
        reminderDates: [1],
        loading: true,
        saving: false
    })
    const { reminderDates, loading, saving } = state

    useEffect(() => {
        Apis({
            module: 'Properties',
            action: 'getPropertyReminderDates',
            _id: selectedProperty?._id
        }).then((data: any) => {
            setState({ ...state, reminderDates: data?.length ? data : [1], loading: false })
        }).catch(() => {
            setState({ ...state, loading: false })
        })
        // eslint-disable-next-line
    }, [])

    const toggleDay = (day: number) => {
        if (day === 1)
            return
        const isSelected = reminderDates.includes(day)
        if (isSelected)
            setState({ ...state, reminderDates: reminderDates.filter(d => d !== day) })
        else if (reminderDates.length < MAX_DATES)
            setState({ ...state, reminderDates: [...reminderDates, day].sort((a, b) => a - b) })
        else
            EventsEmit('app', {
                info: {
                    message: `You can only select up to ${MAX_DATES} reminder dates.`,
                    status: 'error'
                },
            })
    }

    const handleSave = () => {
        setState({ ...state, saving: true })
        Apis({
            module: 'Properties',
            action: 'addUpdate',
            _id: selectedProperty?._id,
            reminderDates
        }).then(({ message }: any) => {
            setState({ ...state, saving: false })
            EventsEmit('app', {
                info: {
                    message: message || 'Reminder dates have been updated.',
                    status: 'success'
                },
            })
        }).catch(() => {
            setState({ ...state, saving: false })
        })
    }

    return (
        <Box sx={{ height: '100%', pr: 5 }}>
            <PageTitle title={"Reminder Dates"} />
            {
                loading ?
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                        <CircularProgress />
                    </Box> :
                    <Box sx={{ maxWidth: 600, mt: 2 }}>
                        <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                            Select up to {MAX_DATES} days of the month to send rent reminders to tenants. The 1st is always included.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 4 }}>
                            {
                                DAYS.map(day => {
                                    const isSelected = reminderDates.includes(day)
                                    return (
                                        <Chip
                                            key={day}
                                            label={day}
                                            clickable={day !== 1}
                                            onClick={() => toggleDay(day)}
                                            color={isSelected ? 'primary' : 'default'}
                                            variant={isSelected ? 'filled' : 'outlined'}
                                            sx={{ width: 44, opacity: day === 1 ? 0.8 : 1 }}
                                        />
                                    )
                                })
                            }
                        </Box>
                        <LoadingButton
                            variant="contained"
                            sx={{ textTransform: 'none' }}
                            loading={saving}
                            onClick={handleSave}
                        >
                            Save
                        </LoadingButton>
                    </Box>
            }
        </Box>
    )
}

export default Connect(mapStateToProps)(ReminderDates)
